import React, { useState, useEffect } from 'react';
import { FaArrowUp, FaWhatsapp } from 'react-icons/fa';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Education from './components/Education';
import Experience from './components/Experience';
import Skills from './components/Skills';
import Projects from './components/Projects';
import PortfolioShowcase from './components/PortfolioShowcase';
import Research from './components/Research';
import Certifications from './components/Certifications';
import Blog from './components/Blog';
import Community from './components/Community';
import SessionVideos from './components/SessionVideos';
import ContentCreation from './components/ContentCreation';
import Contact from './components/Contact';
import IndiaVisit from './components/IndiaVisit';
import IndiaHighlightSection from './components/IndiaHighlightSection';
import SEO from './components/SEO';
import './index.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentView, setCurrentView] = useState(() => {
    const hash = window.location.hash;
    return (hash === '#india-visit' || hash === '#india' || hash.startsWith('#india-post-')) ? 'india' : 'main';
  });

  useEffect(() => {
    // Hide loader after a short delay
    const loaderTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    // Hash change handler for page navigation
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#india-visit' || hash === '#india' || hash.startsWith('#india-post-')) {
        setCurrentView('india');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setCurrentView('main');
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    // Custom Cursor Logic
    const cur = document.getElementById('cursor');
    const ring = document.getElementById('cursor-ring');
    let mx = 0, my = 0, rx = 0, ry = 0;
    let requestRef;



    const handleMouseMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      if (cur) {
        cur.style.left = mx + 'px';
        cur.style.top = my + 'px';
      }
    };

    const animRing = () => {
      rx += (mx - rx) * 0.12;
      ry += (my - ry) * 0.12;
      if (ring) {
        ring.style.left = rx + 'px';
        ring.style.top = ry + 'px';
      }
      requestRef = requestAnimationFrame(animRing);
    };

    document.addEventListener('mousemove', handleMouseMove);
    requestRef = requestAnimationFrame(animRing);

    // Hover effect bindings
    const handleMouseOver = (e) => {
      if (e.target.closest('a, button, .proj-card, .exp-item, .cert-item, .skill-item, .blog-card, .india-preview-card, .gallery-item')) {
        document.body.classList.add('cursor-hover');
      }
    };

    const handleMouseOut = (e) => {
      if (e.target.closest('a, button, .proj-card, .exp-item, .cert-item, .skill-item, .blog-card, .india-preview-card, .gallery-item')) {
        document.body.classList.remove('cursor-hover');
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    // Scroll Reveal Logic — safe and immediate
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
        }
      });
    }, { threshold: 0.01, rootMargin: '0px 0px 80px 0px' });

    const observeAll = () => {
      document.querySelectorAll('.reveal').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight + 150) {
          el.classList.add('in');
        } else {
          obs.observe(el);
        }
      });
    };

    // Initial check + delayed checks
    observeAll();
    setTimeout(observeAll, 100);
    setTimeout(observeAll, 500);
    setTimeout(observeAll, 1500);

    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(requestRef);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener('scroll', handleScroll);
      obs.disconnect();
      clearTimeout(loaderTimer);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenIndiaVisit = () => {
    window.location.hash = '#india-visit';
    setCurrentView('india');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    window.location.hash = '#hero';
    setCurrentView('main');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {isLoading && (
        <div className="preloader">
          <div className="pl-simple-text">bijonn.pages.dev</div>
        </div>
      )}
      <SEO />

      <div id="cursor"></div>
      <div id="cursor-ring"></div>

      {currentView === 'india' ? (
        <IndiaVisit onBackToHome={handleBackToHome} />
      ) : (
        <>
          <Navbar />
          <main>
            <Hero />
            <About />
            <Education />
            <Experience />
            <Skills />
            <Projects />
            <IndiaHighlightSection onOpenIndiaVisit={handleOpenIndiaVisit} />
            <Research />
            <Certifications />
            <Blog />
            <PortfolioShowcase />
            <Community />
            <SessionVideos />
            <ContentCreation />
            <Contact />
          </main>
        </>
      )}

      <a href="https://wa.me/94773155125" target="_blank" rel="noopener noreferrer" className="wa-float" title="Chat on WhatsApp">
        <FaWhatsapp />
      </a>

      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} title="Go to top">
          <FaArrowUp />
        </button>
      )}

      <footer>
        <div className="ft">© 2026 Marisilin Bijosilin · Jaffna, Sri Lanka</div>
        <div className="ft">
          <a href="https://github.com/Bijon2002" aria-label="Marisilin Bijosilin GitHub Profile">GitHub</a> ·
          <a href="https://linkedin.com/in/marisilin-bijosilin" aria-label="Marisilin Bijosilin LinkedIn Profile">LinkedIn</a> ·
          <a href="https://www.facebook.com/bijon.bijosiilin/" aria-label="Marisilin Bijosilin Facebook Profile">Facebook</a>
        </div>
      </footer>
    </>
  );
}

export default App;
